import { useMemo, useState } from "react";
import { X, MapPin, Calendar } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

/**
 * Backstage gallery for /infraestruturas page.
 * Shows real (or placeholder AI) photos of TVCabSat field operations:
 * tower installs, offshore platforms, VSAT alignments, HUET/BST training.
 *
 * Filter by category, grid layout, click for lightbox with caption.
 */

// ──────────────────────────────────────────────────────────────────────────────
// Photo metadata. Captions are i18n. To swap in a real photo: replace the
// `src` value (file in /app/frontend/public/backstage/) — the layout adapts.
// ──────────────────────────────────────────────────────────────────────────────

const PHOTOS = [
  // ───── REAL TVCabSat field photos ─────
  {
    id: "real-microondas-01",
    src: "/backstage/real-microondas-01.png",
    cat: "torres",
    location: { pt: "Angola · Site real", en: "Angola · Field site", es: "Angola · Sitio real" },
    year: "2024",
    caption: {
      pt: "Alinhamento de antena de micro-ondas — equipa em torre",
      en: "Microwave antenna alignment — crew on tower",
      es: "Alineación de antena de microondas — equipo en torre",
    },
  },
  {
    id: "real-vsat-offshore",
    src: "/backstage/real-vsat-offshore.png",
    cat: "offshore",
    location: { pt: "Plataforma offshore · Atlântico", en: "Offshore platform · Atlantic", es: "Plataforma offshore · Atlántico" },
    year: "2024",
    caption: {
      pt: "Alinhamento de VSAT marítimo no convés da plataforma",
      en: "Maritime VSAT alignment on platform deck",
      es: "Alineación de VSAT marítimo en cubierta de plataforma",
    },
  },
  {
    id: "real-microondas-03",
    src: "/backstage/real-microondas-03.png",
    cat: "torres",
    location: { pt: "Angola · Site real", en: "Angola · Field site", es: "Angola · Sitio real" },
    year: "2024",
    caption: {
      pt: "Alinhamento de micro-ondas — escalada em torre treliçada",
      en: "Microwave alignment — climbing lattice tower",
      es: "Alineación de microondas — escalada en torre de celosía",
    },
  },
  {
    id: "real-torre-01",
    src: "/backstage/real-torre-01.png",
    cat: "torres",
    location: { pt: "Angola · Site real", en: "Angola · Field site", es: "Angola · Sitio real" },
    year: "2024",
    caption: {
      pt: "Torre operacional com antenas de micro-ondas e escada de acesso",
      en: "Operational tower with microwave antennas and access ladder",
      es: "Torre operativa con antenas de microondas y escalera de acceso",
    },
  },
  {
    id: "real-torre-02",
    src: "/backstage/real-torre-02.png",
    cat: "torres",
    location: { pt: "Angola · Site real", en: "Angola · Field site", es: "Angola · Sitio real" },
    year: "2024",
    caption: {
      pt: "Instalação de antena no topo de mastro elevado",
      en: "Antenna installation at the top of a high mast",
      es: "Instalación de antena en lo alto de un mástil",
    },
  },
  // ───── AI-generated placeholders (replace when more real photos arrive) ─────
  {
    id: "torres-01",
    src: "/backstage/torres-01.png",
    cat: "torres",
    location: { pt: "Soyo · Zaire", en: "Soyo · Zaire", es: "Soyo · Zaire" },
    year: "2024",
    caption: {
      pt: "Instalação de antena sectorial — torre autoportante de 30 m",
      en: "Sectoral antenna install — 30 m self-supporting tower",
      es: "Instalación de antena sectorial — torre autoportante de 30 m",
    },
  },
  {
    id: "torres-02",
    src: "/backstage/torres-02.png",
    cat: "torres",
    location: { pt: "Cabinda", en: "Cabinda", es: "Cabinda" },
    year: "2024",
    caption: {
      pt: "Aperto de bracket de antena de microondas a 40 m de altura",
      en: "Microwave dish bracket torque-up at 40 m",
      es: "Apriete del soporte de antena de microondas a 40 m",
    },
  },
  {
    id: "torres-03",
    src: "/backstage/torres-03.png",
    cat: "torres",
    location: { pt: "Malanje · Interior", en: "Malanje · Interior", es: "Malanje · Interior" },
    year: "2023",
    caption: {
      pt: "Torre estaiada de 24 m — ancoragem e shelter operacional",
      en: "24 m guyed tower — anchoring and operational shelter",
      es: "Torre con vientos de 24 m — anclaje y shelter operacional",
    },
  },
  {
    id: "offshore-01",
    src: "/backstage/offshore-01.png",
    cat: "offshore",
    location: { pt: "Bloco 17 · Atlântico", en: "Block 17 · Atlantic", es: "Bloque 17 · Atlántico" },
    year: "2024",
    caption: {
      pt: "Mobilização de equipa via helicóptero — plataforma offshore",
      en: "Crew mobilisation by helicopter — offshore platform",
      es: "Movilización de equipo en helicóptero — plataforma offshore",
    },
  },
  {
    id: "offshore-02",
    src: "/backstage/offshore-02.png",
    cat: "offshore",
    location: { pt: "Soyo · Mar", en: "Soyo · Sea", es: "Soyo · Mar" },
    year: "2023",
    caption: {
      pt: "Plataforma de produção e barco de apoio em manobra",
      en: "Production platform and support vessel manoeuvring",
      es: "Plataforma de producción y barco de apoyo en maniobra",
    },
  },
  {
    id: "offshore-03",
    src: "/backstage/offshore-03.png",
    cat: "offshore",
    location: { pt: "Bloco 15", en: "Block 15", es: "Bloque 15" },
    year: "2024",
    caption: {
      pt: "Configuração de VSAT marítimo no convés superior",
      en: "Maritime VSAT configuration on upper deck",
      es: "Configuración de VSAT marítimo en cubierta superior",
    },
  },
  {
    id: "vsat-01",
    src: "/backstage/vsat-01.png",
    cat: "vsat",
    location: { pt: "Luanda · Talatona", en: "Luanda · Talatona", es: "Luanda · Talatona" },
    year: "2024",
    caption: {
      pt: "Alinhamento de antena VSAT banda C 2,4 m",
      en: "VSAT C-band 2.4 m antenna alignment",
      es: "Alineación de antena VSAT banda C 2,4 m",
    },
  },
  {
    id: "vsat-02",
    src: "/backstage/vsat-02.png",
    cat: "vsat",
    location: { pt: "Luanda · Edifício corporativo", en: "Luanda · Corporate building", es: "Luanda · Edificio corporativo" },
    year: "2023",
    caption: {
      pt: "Conjunto de antenas 1.8 / 2.4 / 3.8 m em cobertura",
      en: "Set of 1.8 / 2.4 / 3.8 m antennas on rooftop",
      es: "Conjunto de antenas 1,8 / 2,4 / 3,8 m en cubierta",
    },
  },
  {
    id: "vsat-03",
    src: "/backstage/vsat-03.png",
    cat: "vsat",
    location: { pt: "NOC Luanda", en: "NOC Luanda", es: "NOC Luanda" },
    year: "2024",
    caption: {
      pt: "Sala técnica VSAT — modems iDirect e Comtech operacionais",
      en: "VSAT technical room — iDirect & Comtech modems live",
      es: "Sala técnica VSAT — modems iDirect y Comtech operativos",
    },
  },
  {
    id: "huet-01",
    src: "/backstage/huet-01.png",
    cat: "huet",
    location: { pt: "Centro Oil & Gas", en: "Oil & Gas Centre", es: "Centro Oil & Gas" },
    year: "2024",
    caption: {
      pt: "Treino HUET — Helicopter Underwater Escape Training",
      en: "HUET — Helicopter Underwater Escape Training",
      es: "Entrenamiento HUET — Helicopter Underwater Escape Training",
    },
  },
  {
    id: "huet-02",
    src: "/backstage/huet-02.png",
    cat: "huet",
    location: { pt: "Centro de formação Luanda", en: "Luanda training centre", es: "Centro de formación Luanda" },
    year: "2024",
    caption: {
      pt: "Sessão BST — Basic Safety Training",
      en: "BST — Basic Safety Training session",
      es: "Sesión BST — Basic Safety Training",
    },
  },
  {
    id: "huet-03",
    src: "/backstage/huet-03.png",
    cat: "huet",
    location: { pt: "Campo de treino", en: "Training field", es: "Campo de entrenamiento" },
    year: "2023",
    caption: {
      pt: "Exercício TRA — rapel e resgate em torre",
      en: "TRA exercise — tower rappel & rescue",
      es: "Ejercicio TRA — rápel y rescate en torre",
    },
  },
];

// ──────────────────────────────────────────────────────────────────────────────

const COPY = {
  pt: {
    overline: "Bastidores TVCabSat",
    title: "Instalação de Equipamentos em Torres",
    subtitle:
      "Imagens reais das nossas equipas em obra — torres, plataformas offshore, VSAT e formações de segurança certificadas.",
    all: "Todos",
    cats: { torres: "Torres", offshore: "Offshore", vsat: "VSAT", huet: "HUET / BST" },
    count: (n) => `${n} ${n === 1 ? "foto" : "fotos"}`,
    note: "Galeria atualizada periodicamente conforme novos projectos.",
  },
  en: {
    overline: "TVCabSat Behind-the-Scenes",
    title: "Equipment Installation on Towers",
    subtitle:
      "Real images of our teams in the field — towers, offshore platforms, VSAT and certified safety training.",
    all: "All",
    cats: { torres: "Towers", offshore: "Offshore", vsat: "VSAT", huet: "HUET / BST" },
    count: (n) => `${n} ${n === 1 ? "photo" : "photos"}`,
    note: "Gallery updated periodically as new projects are delivered.",
  },
  es: {
    overline: "Tras las cámaras TVCabSat",
    title: "Instalación de Equipos en Torres",
    subtitle:
      "Imágenes reales de nuestros equipos en obra — torres, plataformas offshore, VSAT y formaciones de seguridad certificadas.",
    all: "Todos",
    cats: { torres: "Torres", offshore: "Offshore", vsat: "VSAT", huet: "HUET / BST" },
    count: (n) => `${n} ${n === 1 ? "foto" : "fotos"}`,
    note: "Galería actualizada periódicamente conforme nuevos proyectos.",
  },
};

const CATEGORIES = ["torres", "offshore", "vsat", "huet"];

export const BackstageGallery = () => {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.pt;
  const [active, setActive] = useState("all");
  const [open, setOpen] = useState(null);

  const filtered = useMemo(
    () => (active === "all" ? PHOTOS : PHOTOS.filter((p) => p.cat === active)),
    [active]
  );

  return (
    <section
      id="backstage"
      data-testid="backstage-section"
      className="relative border-t border-slate-200 bg-[#e8f1fa] py-20 lg:py-28"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <div
              className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-700"
              data-testid="backstage-overline"
            >
              {t.overline}
            </div>
            <h2
              data-testid="backstage-title"
              className="font-heading mt-5 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
            >
              {t.title}
            </h2>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-700 md:text-base">
              {t.subtitle}
            </p>
          </div>
          <div className="rounded-lg border border-blue-200 bg-white/70 px-4 py-2.5 font-mono text-xs font-semibold uppercase tracking-wider text-blue-700 backdrop-blur">
            {t.count(filtered.length)}
          </div>
        </div>

        {/* Filter chips */}
        <div className="mt-10 flex flex-wrap gap-2" data-testid="backstage-filters">
          <FilterChip
            active={active === "all"}
            onClick={() => setActive("all")}
            testId="filter-all"
          >
            {t.all} <span className="ml-1.5 opacity-60">· {PHOTOS.length}</span>
          </FilterChip>
          {CATEGORIES.map((cat) => {
            const n = PHOTOS.filter((p) => p.cat === cat).length;
            return (
              <FilterChip
                key={cat}
                active={active === cat}
                onClick={() => setActive(cat)}
                testId={`filter-${cat}`}
              >
                {t.cats[cat]} <span className="ml-1.5 opacity-60">· {n}</span>
              </FilterChip>
            );
          })}
        </div>

        {/* Grid */}
        <div
          className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="backstage-grid"
        >
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`backstage-photo-${p.id}`}
              onClick={() => setOpen(p)}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-200 ring-1 ring-slate-300 transition-all duration-300 hover:ring-2 hover:ring-blue-500 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <img
                src={p.src}
                alt={p.caption[lang] || p.caption.pt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Always-on category badge */}
              <span className="absolute left-3 top-3 rounded-md bg-slate-900/75 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-white backdrop-blur">
                {t.cats[p.cat]}
              </span>
              {/* Caption gradient overlay revealed on hover */}
              <div className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-slate-900/95 via-slate-900/70 to-transparent p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-blue-200">
                  <MapPin className="h-3 w-3" />
                  {p.location[lang] || p.location.pt}
                  <span className="opacity-50">·</span>
                  <Calendar className="h-3 w-3" />
                  {p.year}
                </div>
                <p className="mt-1.5 text-sm font-semibold leading-snug text-white">
                  {p.caption[lang] || p.caption.pt}
                </p>
              </div>
            </button>
          ))}
        </div>

        <p className="mt-10 text-center text-xs italic text-slate-600">{t.note}</p>
      </div>

      {/* Lightbox */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent
          className="max-w-5xl border-slate-200 bg-white p-0 sm:rounded-2xl"
          data-testid="backstage-lightbox"
        >
          {open && (
            <div className="flex flex-col">
              <div className="relative w-full overflow-hidden rounded-t-2xl bg-slate-900">
                <img
                  src={open.src}
                  alt={open.caption[lang] || open.caption.pt}
                  className="h-auto max-h-[75vh] w-full object-contain"
                />
                <button
                  type="button"
                  onClick={() => setOpen(null)}
                  data-testid="lightbox-close"
                  className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-slate-900 shadow-lg transition hover:bg-white"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
                <span className="absolute left-3 top-3 rounded-md bg-slate-900/85 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white">
                  {t.cats[open.cat]}
                </span>
              </div>
              <div className="px-6 py-5 sm:px-8 sm:py-6">
                <DialogTitle className="font-heading text-lg font-bold text-slate-900 sm:text-xl">
                  {open.caption[lang] || open.caption.pt}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-wider text-blue-700">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />
                      {open.location[lang] || open.location.pt}
                    </span>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {open.year}
                    </span>
                  </div>
                </DialogDescription>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const FilterChip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-wider transition-all duration-200 ${
      active
        ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
        : "border border-slate-300 bg-white/80 text-slate-700 hover:border-blue-400 hover:bg-white hover:text-blue-700"
    }`}
  >
    {children}
  </button>
);
